import React, { useRef } from "react";
import { formatLegacyScheduleHoursLabel } from "./clientScheduleGridUtils";
import ClientScheduleClientGroup from "./ClientScheduleClientGroup";

export default function ClientScheduleGrid({
  scheduleDays = [],
  expandedCards = {},
  onToggleCard,
  onShowNote,
  loading = false,
  searchTrimmed = "",
}) {
  const scrollbarRef = useRef(null);
  const contentRef = useRef(null);

  const handleScrollbarScroll = (e) => {
    if (contentRef.current) {
      contentRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleContentScroll = (e) => {
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const visibleDays = scheduleDays.filter(
    (day) => !searchTrimmed || (day.groups && day.groups.length > 0),
  );

  if (loading && visibleDays.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        Loading schedule…
      </div>
    );
  }

  if (!loading && visibleDays.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No shifts found for the selected filters.
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div
        ref={scrollbarRef}
        className="w-full overflow-x-auto mb-3 h-3 flex-shrink-0"
        onScroll={handleScrollbarScroll}
        style={{ scrollbarWidth: "thin", scrollbarColor: "#6b7280 #e5e7eb" }}
      >
        <div className="min-w-max h-1">
          {visibleDays.map((day, dayIdx) => (
            <div
              key={`scroll-${day.isoDate}-${dayIdx}`}
              className="inline-block w-80 mr-4"
            />
          ))}
        </div>
      </div>

      <div
        ref={contentRef}
        className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
        onScroll={handleContentScroll}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <div className="flex gap-4 min-w-max h-full">
          {visibleDays.map((day, dayIdx) => (
            <div
              key={`${day.isoDate}-${dayIdx}`}
              className="flex flex-col w-80 flex-shrink-0 h-full"
            >
              <div className="bg-sidebar-active text-white rounded-lg p-2 mb-2 sticky top-0 z-10 flex-shrink-0">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="font-bold text-sm">{day.day}</div>
                    <div className="text-xs opacity-90">{day.date}</div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <div className="text-center">
                      <div className="font-bold text-xs">
                        {formatLegacyScheduleHoursLabel(day.totalHours)}
                      </div>
                      <div className="text-xs opacity-90">HOURS</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-xs">
                        {day.confirmedShifts ||
                          `${day.confirmedCount}/${day.requiredStaff}`}
                      </div>
                      <div className="text-xs opacity-90">CONFIRMED</div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-2"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#6b7280 #e5e7eb",
                }}
              >
                {(day.groups || []).length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                    <div className="text-center">
                      <div>No shifts scheduled</div>
                    </div>
                  </div>
                ) : (
                  (day.groups || []).map((group, groupIdx) => {
                    const cardKey = `${dayIdx}-${groupIdx}`;
                    return (
                      <ClientScheduleClientGroup
                        key={cardKey}
                        group={group}
                        cardKey={cardKey}
                        isExpanded={!!expandedCards[cardKey]}
                        onToggle={onToggleCard}
                        onShowNote={onShowNote}
                      />
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
